import React, { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";
import { useCurrentUser } from "@/lib/auth";
import IdleLogoutGuard from "@/components/IdleLogoutGuard";

export default function AppShell({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, user, logout } = useCurrentUser();
  const [location] = useLocation();
  const [scrolled, setScrolled] = useState(false);

  const isLanding = location === "/";

  // Sur la landing, la nav est transparente au-dessus du hero plein cadre puis
  // devient blanche au defilement. Ailleurs, elle reste blanche (sticky).
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const transparent = isLanding && !scrolled;

  return (
    <div className="min-h-[100dvh] flex flex-col bg-cream font-body">
      {/* Auto-déconnexion par inactivité (ne s'arme que si authentifié). */}
      <IdleLogoutGuard />
      <nav
        className={`${isLanding ? "fixed inset-x-0" : "sticky"} top-0 z-50 px-6 py-3 transition-colors duration-300 ${
          transparent
            ? "bg-transparent border-b border-transparent"
            : "bg-white/95 backdrop-blur-md border-b border-orange-100 shadow-sm"
        }`}
      >
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link href="/" className="flex items-center gap-3">
            <img
              src="/brand/logo-catho-school.webp"
              alt="Catho School"
              className={`h-9 transition-all ${transparent ? "brightness-0 invert" : ""}`}
            />
          </Link>

          <div className="flex items-center gap-4 text-sm font-medium">
            <Link
              href="/ecoles"
              className={`transition-colors ${transparent ? "text-white hover:text-gold" : "text-navy hover:text-coral"}`}
            >
              Trouver mon école
            </Link>

            {isAuthenticated ? (
              <>
                <Link
                  href="/dashboard"
                  className={`transition-colors ${transparent ? "text-white hover:text-gold" : "text-navy hover:text-coral"}`}
                >
                  Tableau de bord
                </Link>
                <div
                  className={`flex items-center gap-3 pl-4 border-l ${transparent ? "border-white/30" : "border-orange-100"}`}
                >
                  <div className="text-right hidden sm:block">
                    <p className={`text-sm font-bold ${transparent ? "text-white" : "text-navy"}`}>
                      {user?.prenoms} {user?.nom}
                    </p>
                  </div>
                  <button
                    onClick={logout}
                    className={`text-xs ${transparent ? "text-white/80 hover:text-white" : "text-red-500 hover:text-red-700"}`}
                  >
                    Déconnexion
                  </button>
                </div>
              </>
            ) : (
              <div
                className={`flex items-center gap-3 pl-4 border-l ${transparent ? "border-white/30" : "border-orange-100"}`}
              >
                <Link
                  href="/auth/login"
                  className={`font-bold transition-colors ${transparent ? "text-white hover:text-gold" : "text-navy hover:text-coral"}`}
                >
                  Connexion
                </Link>
                <Link
                  href="/auth/register"
                  className="bg-coral text-white hover:bg-[#d16b50] px-4 py-2 rounded-full font-bold transition-colors"
                >
                  S'inscrire
                </Link>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="flex-1">{children}</main>

      <footer className="bg-white border-t border-orange-100 py-8 px-6 mt-12">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex gap-4 items-center">
            <img
              src="/brand/logo-catho-school.webp"
              alt="Catho School"
              className="h-8 grayscale opacity-60"
            />
            <img
              src="/brand/logo-sedec.webp"
              alt="SEDEC"
              className="h-8 grayscale opacity-60"
            />
          </div>
          <div className="text-sm text-navy/50">
            © 2026 Catho School. Tous droits réservés. Une initiative de
            l'Économat Diocésain d'Abidjan.
          </div>
        </div>
      </footer>
    </div>
  );
}
